/**
 * Phase 88 — Détail d'une tournée de réappro.
 * Route : /tournees-reappro/:id
 *
 * Une seule vue qui regroupe :
 *  - Pour chaque distributeur sélectionné : la liste des produits avec leur stock actuel,
 *    la capacité max du distrib, la qty suggérée, qty planifiée (édit) et qty chargée (édit + scan)
 *  - Un input de scan EAN13 global (sélectionne d'abord un distrib, puis scanne)
 *  - Bouton « Valider chargement » (BROUILLON → EN_PREPA)
 *  - Bouton « Imprimer Rapport de remplissage »
 *
 * Lot 1 (présent) : préparation + impression. Pas de mouvement de stock auto.
 * Lot 2 (futur) : saisie sur place + création StockLoss automatique.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer, Loader2, ScanLine, CheckCircle2, AlertCircle, Trash2, FileDown, XCircle, Lock, Undo2, Layers, FileText, Camera, Smartphone, Search, RefreshCw } from "lucide-react";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PhantomLinesDiagnostic from "../components/PhantomLinesDiagnostic";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { LotAllocationDialog } from "@/components/LotAllocationDialog";

// Phase 92 — Seuil minimal de barquettes pour qu'une visite distributeur "vaille la peine"
const MIN_QTY_PER_DISTRIB = 2;

const STATUS_META = {
  BROUILLON:    { label: "Brouillon", cls: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]" },
  EN_PREPA:     { label: "En préparation", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
  EN_LIVRAISON: { label: "En livraison", cls: "bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]" },
  CLOTUREE:     { label: "Clôturée", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
};

export default function RefillTourDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tour, setTour] = useState(null);
  const [scanDistribId, setScanDistribId] = useState("");
  const [scanInput, setScanInput] = useState("");
  // Phase 101gf (Feb 2026) — Quantité par-scan paramétrable (défaut 1). Permet
  // de scanner UNE fois pour +N au lieu de scanner/cliquer N fois.
  const [scanQty, setScanQty] = useState(1);
  // Undo du dernier scan en 1 clic — mémorise aussi la quantité pour la restituer.
  const [lastScan, setLastScan] = useState(null);
  // Phase 101gf — Dialog « + Slot manquant » : liste des slots configurés
  // sur le distributeur mais absents de la tournée (effacés par erreur).
  const [missingSlotsDialog, setMissingSlotsDialog] = useState(null); // { distributor_id, distributor_name, missing: [{slot_number, product_id, product_name}] }
  const [missingSlotsLoading, setMissingSlotsLoading] = useState(false);
  // Phase 98aw (10/07/2026) — Ouverture du scanner caméra en secours (utile si le
  // lecteur USB ne lit plus les EAN13 sur la tablette — plainte terrain).
  const [cameraOpen, setCameraOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [locking, setLocking] = useState(false);
  // Phase 98x — Sync single-entry AgroManage → Nayax MOMA au verrouillage.
  // Phase 101bf (14/02/2026) — Default `false` + toggle masqué sauf si Settings
  // `nayax_expert_push_mode = true` (mode expert admin). Ton équipe TikTok
  // n'a plus à cocher/décocher sans savoir ce que ça fait.
  const [pushNayax, setPushNayax] = useState(false);
  const [expertPushMode, setExpertPushMode] = useState(false);
  useEffect(() => {
    let mounted = true;
    api.get("/settings").then((r) => {
      if (mounted) setExpertPushMode(Boolean(r.data?.nayax_expert_push_mode));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  // Phase 98 — Dialog de choix des lots pour une ligne
  const [lotDialogLine, setLotDialogLine] = useState(null); // {distributor_id, product_id, product_name, slot_number, qty_target, initialAllocations}
  // Phase 98ay — Modale de diagnostic des lignes fantômes (produit "23")
  const [phantomOpen, setPhantomOpen] = useState(false);
  const scanRef = useRef(null);

  const [drinksBusy, setDrinksBusy] = useState(false);
  const drinksFromNayax = async () => {
    setDrinksBusy(true);
    try {
      const { data } = await api.post(`/refill-tours/${id}/drinks-from-nayax`);
      setTour(data.tour);
      toast.success(data.updated ? `${data.updated} slot(s) boissons mis à jour selon Nayax` : "Nayax n'a rien recommandé pour les boissons");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nayax indisponible");
    } finally { setDrinksBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/refill-tours/${id}`);
      setTour(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement tournée");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  // Lignes groupées par distributeur
  const linesByDistrib = useMemo(() => {
    if (!tour) return {};
    const grp = {};
    for (const ln of tour.lines || []) {
      const did = ln.distributor_id;
      if (!grp[did]) {
        grp[did] = {
          distributor_id: did,
          distributor_name: ln.distributor_name,
          distributor_code: ln.distributor_code,
          distributor_city: ln.distributor_city,
          lines: [],
        };
      }
      grp[did].lines.push(ln);
    }
    // Trier les produits par slot (ordre physique machine) puis par nom
    Object.values(grp).forEach((g) => g.lines.sort((a, b) => {
      const sa = parseInt(String(a.slot_number || a.slot || "9999").split(/[,/]/)[0], 10);
      const sb = parseInt(String(b.slot_number || b.slot || "9999").split(/[,/]/)[0], 10);
      if (sa !== sb) return sa - sb;
      return (a.product_name || "").localeCompare(b.product_name || "");
    }));
    return grp;
  }, [tour]);

  // Total chargé par produit (toutes destinations) — pour vérifier vs stock central
  const loadedByProduct = useMemo(() => {
    if (!tour) return {};
    const totals = {};
    for (const ln of tour.lines || []) {
      totals[ln.product_id] = (totals[ln.product_id] || 0) + Number(ln.qty_loaded || 0);
    }
    return totals;
  }, [tour]);

  const updateLine = async (distributor_id, product_id, slot_number, patch) => {
    // Optimistic update — Phase 98 matche par (distrib, product, slot)
    const slotKey = String(slot_number || "");
    setTour((prev) => prev && {
      ...prev,
      lines: prev.lines.map((ln) =>
        ln.distributor_id === distributor_id
          && ln.product_id === product_id
          && String(ln.slot_number || "") === slotKey
            ? { ...ln, ...patch }
            : ln,
      ),
    });
    try {
      await api.put(`/refill-tours/${id}/line`, {
        distributor_id,
        product_id,
        slot_number: slot_number || "",
        ...patch,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur mise à jour");
      load(); // resync
    }
  };

  // Phase 101gf — Ouvre le dialog des slots manquants pour un distributeur
  const openMissingSlots = async (distributor_id, distributor_name) => {
    setMissingSlotsLoading(true);
    setMissingSlotsDialog({ distributor_id, distributor_name, missing: [] });
    try {
      const { data } = await api.get(`/refill-tours/${id}/missing-slots`, {
        params: { distributor_id },
      });
      const missing = data.missing_slots || [];
      if (missing.length === 0) {
        toast.info("Aucun slot manquant — tous les slots configurés sont présents dans la tournée.");
        setMissingSlotsDialog(null);
        return;
      }
      setMissingSlotsDialog({ distributor_id, distributor_name, missing });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement slots");
      setMissingSlotsDialog(null);
    } finally {
      setMissingSlotsLoading(false);
    }
  };

  const restoreSlot = async (distributor_id, slot_number) => {
    try {
      const { data } = await api.post(`/refill-tours/${id}/restore-slot`, null, {
        params: { distributor_id, slot_number },
      });
      if (data.already_present) {
        toast.info(`Slot ${slot_number} déjà présent dans la tournée.`);
      } else {
        toast.success(`Slot ${slot_number} restauré : ${data.product_name}`);
      }
      // Rafraîchit le dialog ET le tour (la ligne restaurée doit disparaître de la liste des manquants)
      load();
      if (missingSlotsDialog) {
        setMissingSlotsDialog({
          ...missingSlotsDialog,
          missing: missingSlotsDialog.missing.filter((m) => m.slot_number !== slot_number),
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur restauration slot");
    }
  };

  // Phase 98 — Supprime une ligne (avec confirmation renforcée Phase 101gf)
  const deleteLine = async (line) => {
    const hasAllocs = (line.lot_allocations || []).length > 0;
    const qtyPlan = Number(line.qty_planned || 0);
    const warnLines = [
      `⚠️ ATTENTION — Suppression IRRÉVERSIBLE sans sortir du programme :`,
      ``,
      `  • Produit : ${line.product_name || "?"}`,
      `  • Slot    : ${line.slot_number || "?"}`,
      `  • Planifié : ${qtyPlan}`,
      hasAllocs ? `  • ${line.lot_allocations.length} allocation(s) de lot(s) seront perdues` : null,
      ``,
      `Pour la récupérer après coup : re-scannez l'EAN13 du produit (la ligne se recrée vide, sans ses lots).`,
    ].filter(Boolean).join("\n");
    const ok = await confirm({
      title: "Supprimer cette ligne de slot ?",
      description: warnLines,
      confirmText: "Oui, supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/refill-tours/${id}/line`, {
        params: {
          distributor_id: line.distributor_id,
          product_id: line.product_id,
          slot_number: line.slot_number || "",
        },
      });
      toast.success(`Ligne supprimée · pour la régénérer : scannez l'EAN13 du produit`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  // Phase 98 — Sauvegarde les lot_allocations d'une ligne (depuis LotAllocationDialog)
  const saveLotAllocations = async (allocations) => {
    if (!lotDialogLine) return;
    const { distributor_id, product_id, slot_number } = lotDialogLine;
    try {
      await api.put(`/refill-tours/${id}/line`, {
        distributor_id,
        product_id,
        slot_number: slot_number || "",
        lot_allocations: allocations,
      });
      toast.success(`${allocations.length} lot(s) attribué(s)`);
      setLotDialogLine(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement lots");
    }
  };

  const submitScan = async (e, overrideEan) => {
    e?.preventDefault?.();
    const ean = (overrideEan ?? scanInput ?? "").trim();
    if (!ean) return;
    if (!scanDistribId) {
      toast.error("Sélectionnez d'abord le distributeur destinataire");
      return;
    }
    // Phase 101gf — Vide l'input AVANT l'appel API : si le scan échoue, le
    // prochain scan ne se concatène pas sur l'ancien EAN resté bloqué.
    setScanInput("");
    try {
      const { data } = await api.post(`/refill-tours/${id}/scan`, {
        ean13: ean,
        distributor_id: scanDistribId,
      });
      toast.success(`+1 ${data.product.name} → ${data.distributor.name}`);
      setLastScan({ ean, distributor_id: scanDistribId, product_name: data.product.name });
      // Refresh full tour (might have added a new line)
      load();
      // Phase 101gf — Re-focus intelligent : seulement si pas en train de taper ailleurs
      setTimeout(() => {
        const active = document.activeElement;
        const isTyping = active && active !== scanRef.current && (
          active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT"
        );
        if (!isTyping) scanRef.current?.focus();
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Scan refusé: « ${ean} »`);
    }
  };

  // Phase 101gf — Annule le dernier scan (increment=-1) sans refaire le match EAN.
  const undoLastScan = async () => {
    if (!lastScan) {
      toast.info("Aucun scan à annuler.");
      return;
    }
    try {
      await api.post(`/refill-tours/${id}/scan`, {
        ean13: lastScan.ean,
        distributor_id: lastScan.distributor_id,
        increment: -1,
      });
      toast.success(`−1 ${lastScan.product_name} annulé`);
      setLastScan(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Annulation refusée");
    }
  };

  // Phase 98aw — Handler du scan caméra (BarcodeScanner accepte EAN13 + QR + Code128 + …)
  const handleCameraScan = (decoded) => {
    setCameraOpen(false);
    if (!decoded) return;
    setScanInput(decoded);
    submitScan(null, decoded);
  };

  const validateLoading = async () => {
    setValidating(true);
    try {
      await api.post(`/refill-tours/${id}/validate-loading`);
      toast.success("Chargement validé — vous pouvez maintenant verrouiller & imprimer");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur validation");
    } finally {
      setValidating(false);
    }
  };

  // Phase 97 + 98x — Verrouille la tournée + sort le stock de la centrale + push Nayax (opt) + PDF
  const lockAndPrint = async () => {
    const nayaxTxt = pushNayax
      ? "\n\n📡 Sync Nayax MOMA activé — les quantités par slot seront poussées automatiquement à Nayax (plus besoin de re-saisir dans MOMA)."
      : "";
    const ok = await confirm({
      title: "Verrouiller & Imprimer la tournée ?",
      description: "Cette action est IRRÉVERSIBLE :\n\n• Le stock central sera immédiatement décrémenté SELON LES LOTS MANUELLEMENT ALLOUÉS (aucun FIFO auto)\n• La tournée passe en statut « En livraison »\n• Les quantités ne pourront plus être modifiées\n• Le PDF restera re-téléchargeable sans nouvelle sortie de stock" + nayaxTxt + "\n\nÀ effectuer uniquement quand le box thermo est prêt à partir.",
      confirmText: "Verrouiller & Imprimer",
      destructive: false,
    });
    if (!ok) return;
    setLocking(true);
    try {
      const params = pushNayax ? "?auto_push_nayax=true" : "";
      const { data } = await api.post(`/refill-tours/${id}/lock-and-print${params}`);
      const nbMov = (data.movements_created || []).length;
      const nbWarn = (data.warnings || []).length;
      toast.success(`Tournée verrouillée — ${nbMov} lots sortis du central${nbWarn ? ` · ${nbWarn} avertissements` : ""}`);
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      // Phase 98x + 98ab — Rapport détaillé du sync Nayax (si activé)
      if (data.nayax_sync) {
        const ns = data.nayax_sync;
        const totalOk = ns.success_count || 0;
        const totalSkip = ns.skipped_count || 0;
        const totalFail = ns.failed_count || 0;
        const totalDist = ns.total_distributors || 0;

        if (totalDist === 0) {
          toast.info("📡 Nayax MOMA : aucun distributeur Nayax dans cette tournée (skip)");
        } else if (totalOk === totalDist) {
          // 100% succès
          toast.success(`📡 Nayax MOMA : ${totalOk}/${totalDist} distributeur(s) synchronisé(s)`);
        } else if (totalFail === 0 && totalSkip === totalDist) {
          // Tout skippé — pas un échec, juste config incomplète
          toast.warning(
            `📡 Nayax MOMA : rien à pousser (${totalSkip} distributeur(s) skippé(s) — vérifiez que les produits ont un NayaxProductID)`,
            { duration: 8000 }
          );
        } else if (totalFail > 0 && totalOk > 0) {
          // Mixte succès + échecs
          toast.warning(`📡 Nayax MOMA : ${totalOk}/${totalDist} OK · ${totalFail} en échec — voir détails`);
        } else if (totalFail > 0) {
          // Vrais échecs HTTP sur tous les distribs non skippés
          toast.error(`📡 Nayax MOMA : ${totalFail} distributeur(s) en échec — consultez /parametres/nayax-sync-logs`, { duration: 10000 });
        } else {
          // Cas résiduel (skip + peut-être OK partiel)
          toast.info(`📡 Nayax MOMA : ${totalOk} OK · ${totalSkip} skip · ${totalFail} échec`);
        }

        // Détails par distributeur en échec RÉEL (pas skippé)
        (ns.results || []).forEach((r) => {
          if (!r.ok && !r.skipped) {
            const label = r.distributor_name || r.distributor_id;
            const reason = r.reason || r.body_excerpt || r.error || `HTTP ${r.http_status}`;
            toast.error(`${label} : ${reason}`, { duration: 8000 });
          }
        });
        // Détails des skips (info, pas error)
        (ns.results || []).forEach((r) => {
          if (r.skipped) {
            const label = r.distributor_name || r.distributor_id;
            toast.info(`⏭️ ${label} — skip : ${r.reason}`, { duration: 6000 });
          }
        });
      }
      await load();
      setTimeout(() => window.print(), 200);
    } catch (e) {
      // Phase 101fj — Erreur structurée si allocations manquantes
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "manual_lot_allocation_required") {
        toast.error(detail.message || "Sélection manuelle des lots requise (aucun FIFO auto)", {
          duration: 10000,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur verrouillage");
      }
    } finally {
      setLocking(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  const deleteTour = async () => {
    // Phase 98 — Message adapté selon statut (les CLOTUREE/EN_LIVRAISON gardent
    // les mouvements stock en base pour audit — la suppression du document tour
    // ne rembourse pas la centrale).
    const status = tour?.status || "BROUILLON";
    const isLocked = status === "EN_LIVRAISON" || status === "CLOTUREE";
    const description = isLocked
      ? `⚠️ Tournée en statut ${status}.\n\nLa suppression retire l'en-tête de tournée mais NE REMBOURSE PAS le stock déjà sorti de la centrale. Les mouvements TRANSFER/WASTE associés restent en base pour audit.\n\nUsage recommandé : purger des tournées de test ou anciens historiques.`
      : `Cette tournée (${tour?.reference}) sera supprimée définitivement.`;
    const ok = await confirm({
      title: isLocked ? "🔓 Suppression forcée d'une tournée verrouillée" : "Supprimer la tournée ?",
      description,
      confirmText: isLocked ? "Supprimer quand même" : "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/refill-tours/${id}`);
      toast.success("Tournée supprimée");
      navigate("/tournees-reappro");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  // Phase 92 — Retire toutes les lignes d'un distrib (pas la peine d'aller pour 1 unité)
  const excludeDistrib = async (distributorId, distributorName, totalPlanned) => {
    const ok = await confirm({
      title: "Exclure ce distributeur ?",
      description: `${distributorName} ne sera pas visité (${totalPlanned} barquette${totalPlanned > 1 ? "s" : ""} prévue${totalPlanned > 1 ? "s" : ""} seront retirées de la tournée). Vous pourrez recréer la tournée pour l'inclure plus tard.`,
      confirmText: "Exclure",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/refill-tours/${id}/exclude-distributor`, { distributor_id: distributorId });
      toast.success(`${distributorName} retiré de la tournée`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur exclusion");
    }
  };

  // Phase 92 — Ouvre le PDF livreur d'un distributeur dans un nouvel onglet
  const openDistribPdf = (distributorId) => {
    // L'API renvoie inline application/pdf, le navigateur l'affiche directement
    const url = `${api.defaults.baseURL}/refill-tours/${id}/distributor/${distributorId}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  if (!tour) return <div className="text-[#575A57]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
  const meta = STATUS_META[tour.status] || STATUS_META.BROUILLON;
  // Phase 97 — EN_LIVRAISON = verrouillé (stock sorti). CLOTUREE = fermé.
  // Ni scan, ni édition des qtys ne sont autorisés dans ces 2 statuts.
  const readOnly = tour.status === "CLOTUREE" || tour.status === "EN_LIVRAISON";
  const isDraftEditable = tour.status === "BROUILLON" || tour.status === "EN_PREPA";

  const distribsList = Object.values(linesByDistrib);
  const totalPlanned = tour.lines.reduce((s, ln) => s + Number(ln.qty_planned || 0), 0);
  const totalLoaded = tour.lines.reduce((s, ln) => s + Number(ln.qty_loaded || 0), 0);
  // Phase 101fj — Lignes chargées sans allocation manuelle → blocage lock-and-print
  const missingAllocLines = tour.lines.filter((ln) => {
    const qty = Number(ln.qty_loaded || ln.qty_planned || 0);
    if (qty <= 0 || ln.stock_simple) return false;
    const allocs = ln.lot_allocations || [];
    return allocs.length === 0 || !allocs[0]?.lot_id;
  }).length;

  return (
    <div className="space-y-6" data-testid="refill-tour-detail-page">
      {/* En-tête */}
      <div className="flex items-center gap-4 flex-wrap no-print">
        <Button variant="outline" size="sm" onClick={() => navigate("/tournees-reappro")} data-testid="back-to-tours">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[#1A1C1A] font-mono">{tour.reference}</h1>
          <Badge className={meta.cls}>{meta.label}</Badge>
        </div>
        <div className="flex-1" />
        {!readOnly && tour.lines.some((ln) => ln.stock_simple) && (
          <Button variant="outline" onClick={drinksFromNayax} disabled={drinksBusy} data-testid="drinks-from-nayax-btn">
            {drinksBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Boissons : quantités Nayax
          </Button>
        )}
        {!readOnly && tour.status === "BROUILLON" && (
          <Button onClick={validateLoading} disabled={validating} className="bg-[#1F4E3D] hover:bg-[#173D2F]" data-testid="validate-loading-btn">
            {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Valider le chargement
          </Button>
        )}
        {!readOnly && tour.status === "EN_PREPA" && (
          <div className="flex items-center gap-3">
            {/* Phase 98x — Toggle sync Nayax MOMA au verrouillage
                Phase 101bf — Visible uniquement en mode expert Nayax
                (Paramètres → Nayax → Mode expert push picklist). */}
            {expertPushMode && (
              <label
                className="flex items-center gap-1.5 text-xs text-[#8C6B2F] cursor-pointer bg-[#FBF4E9] px-2 py-1 rounded border border-[#8C6B2F]/30"
                title="Mode expert : pousse la picklist AM vers Nayax MOMA au verrouillage. Utilise avec parcimonie (source de HTTP 500 historiques)."
                data-testid="push-nayax-toggle"
              >
                <input
                  type="checkbox"
                  checked={pushNayax}
                  onChange={(e) => setPushNayax(e.target.checked)}
                  className="accent-[#8C6B2F]"
                  data-testid="push-nayax-checkbox"
                />
                📡 Push Nayax <span className="text-[9px] uppercase tracking-wider ml-0.5">Expert</span>
              </label>
            )}
            <Button
              onClick={lockAndPrint}
              disabled={locking || missingAllocLines > 0}
              className="bg-[#C05C47] hover:bg-[#A04E2C]"
              data-testid="lock-and-print-btn"
              title={missingAllocLines > 0 ? `${missingAllocLines} ligne(s) sans lot alloué — sélection manuelle obligatoire (aucun FIFO auto)` : "Verrouiller la tournée & imprimer"}
            >
              {locking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {missingAllocLines > 0 ? `Sélectionne ${missingAllocLines} lot(s)` : "Verrouiller & Imprimer"}
            </Button>
          </div>
        )}
        {tour.status === "EN_LIVRAISON" && (
          <>
            <Button onClick={() => navigate(`/tournees-reappro/${id}/terrain`)} className="bg-[#2E5C8A] hover:bg-[#245073]" data-testid="tour-onsite-btn" title="Validation sur place depuis la tablette (scan + DLC + reappro)">
              <Smartphone className="w-4 h-4 mr-2" />
              Validation Terrain
            </Button>
            <Button onClick={() => navigate(`/tournees-reappro/${id}/reconcilier`)} className="bg-[#1F4E3D] hover:bg-[#173D2F]" data-testid="tour-reconcile-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Réconcilier tournée
            </Button>
            <Button onClick={() => navigate(`/tournees-reappro/${id}/execute`)} variant="outline" data-testid="tour-return-btn" title="Ancien formulaire (DLC + manquants)">
              <Undo2 className="w-4 h-4 mr-2" />
              Retour tournée (legacy)
            </Button>
            <Button variant="outline" onClick={printReport} data-testid="reprint-refill-report">
              <Printer className="w-4 h-4 mr-2" /> Réimprimer
            </Button>
          </>
        )}
        {/* Phase 96f — Push MOMA supprimé (décision user : synchro descendante uniquement) */}
        {(tour.status === "BROUILLON" || tour.status === "CLOTUREE") && (
          <Button variant="outline" onClick={printReport} data-testid="print-refill-report">
            <Printer className="w-4 h-4 mr-2" /> Imprimer le Rapport
          </Button>
        )}
        {/* Phase 98ar (09/07/2026) — PDF Préparation compact : 1 page A4, sans page de garde,
            une section par distributeur, produit + slot + qté à charger + lot suggéré + DLC + case à cocher.
            Dispo dès qu'il y a des lignes à préparer (BROUILLON, EN_PREPA, EN_LIVRAISON). */}
        {(tour.status === "BROUILLON" || tour.status === "EN_PREPA" || tour.status === "EN_LIVRAISON") && distribsList.length > 0 && totalPlanned > 0 && (
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/tournees-reappro/${id}/prep-tablet`)}
              data-testid="open-prep-tablet-btn"
              className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE] bg-[#EAF4EE]/50"
              title="Écran interactif tablette avec saisie des lots et autosave"
            >
              <Smartphone className="w-4 h-4 mr-2" /> Préparer sur tablette
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`${api.defaults.baseURL}/refill-tours/${id}/prep-picklist.pdf`, "_blank")}
              data-testid="download-prep-picklist-btn"
              className="border-[#1F4E3D]/40 text-[#1F4E3D] hover:bg-[#EAF4EE]"
              title="1 page A4 compacte à imprimer pour préparer le box thermo"
            >
              <FileDown className="w-4 h-4 mr-2" /> PDF Préparation
            </Button>
          </>
        )}
        {/* Phase 98ae — Rapport de retour PDF disponible après clôture */}
        {tour.status === "CLOTUREE" && (
          <Button
            variant="outline"
            className="border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]"
            onClick={() => window.open(`${api.defaults.baseURL}/refill-tours/${id}/return-report.pdf`, "_blank")}
            data-testid="download-return-report-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Rapport de retour PDF
          </Button>
        )}
        {/* Phase 98ay — Diagnostic des lignes fantômes (produit "23" bug) */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPhantomOpen(true)}
          data-testid="tour-diagnose-btn"
          className="border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]"
          title="Analyse les lignes suspectes (produits fantômes / archivés / noms vides)"
        >
          <Search className="w-4 h-4 mr-2" /> Diagnostiquer
        </Button>
        {/* Phase 98 — Corbeille visible tous statuts (dialog adaptatif) */}
        <Button
          variant="outline"
          size="icon"
          onClick={deleteTour}
          data-testid="delete-tour-btn"
          title={(tour.status === "EN_LIVRAISON" || tour.status === "CLOTUREE") ? "Suppression forcée (stock non remboursé)" : "Supprimer la tournée"}
        >
          <Trash2 className="w-4 h-4 text-[#C05C47]" />
        </Button>
      </div>

      {/* Bandeau scan EAN13 */}
      {!readOnly && (
        <Card className="border-[#1F4E3D]/30 bg-[#FAFAF7] no-print">
          <CardContent className="p-4">
            <form onSubmit={submitScan} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-[#575A57] mb-1 block">Distributeur destinataire</Label>
                <select
                  value={scanDistribId}
                  onChange={(e) => setScanDistribId(e.target.value)}
                  className="w-full border border-[#E5E5E0] rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="scan-distrib-select"
                >
                  <option value="">— Sélectionner —</option>
                  {distribsList.map((g) => (
                    <option key={g.distributor_id} value={g.distributor_id}>
                      {g.distributor_name} {g.distributor_city ? `· ${g.distributor_city}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-[2] min-w-[280px]">
                <Label className="text-xs text-[#575A57] mb-1 block flex items-center gap-1">
                  <ScanLine className="w-3 h-3" /> Scanner EAN13 (ou saisir manuellement + Enter)
                </Label>
                <Input
                  ref={scanRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  placeholder="Scannez ou tapez le code EAN13…"
                  className="font-mono"
                  autoFocus
                  inputMode="none"
                  data-testid="scan-input"
                />
              </div>
              <Button type="submit" className="bg-[#1F4E3D] hover:bg-[#173D2F]" data-testid="scan-submit-btn">
                <ScanLine className="w-4 h-4 mr-2" /> +1
              </Button>
              {/* Phase 101gf — Annule le dernier scan (−1) */}
              <Button
                type="button"
                variant="outline"
                onClick={undoLastScan}
                disabled={!lastScan}
                className="border-[#C05C47]/40 text-[#C05C47] hover:bg-[#C05C47]/5"
                data-testid="scan-undo-btn"
                title={lastScan ? `Annule le +1 de ${lastScan.product_name}` : "Aucun scan à annuler"}
              >
                <Undo2 className="w-4 h-4 mr-2" /> Annuler
              </Button>
              {/* Phase 98aw — bouton scan caméra en secours */}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!scanDistribId) {
                    toast.error("Sélectionnez d'abord le distributeur destinataire");
                    return;
                  }
                  setCameraOpen(true);
                }}
                className="border-[#1F4E3D]/40 text-[#1F4E3D] hover:bg-[#EAF4EE]"
                data-testid="scan-camera-btn"
                title="Utiliser la caméra de la tablette pour scanner EAN13 ou QR"
              >
                <Camera className="w-4 h-4 mr-2" /> Caméra
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <BarcodeScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={handleCameraScan}
      />

      {/* Phase 101gf — Dialog restauration slots manquants */}
      <AlertDialog open={!!missingSlotsDialog} onOpenChange={(o) => { if (!o) setMissingSlotsDialog(null); }}>
        <AlertDialogContent data-testid="missing-slots-dialog" className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#1F4E3D]" />
              Slots configurés absents de la tournée
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-left text-sm space-y-3">
                <p className="text-[#575A57]">
                  <strong>{missingSlotsDialog?.distributor_name}</strong> — ces slots sont configurés sur le distributeur mais pas dans la tournée.
                  Clic sur un slot pour le recréer (produit assigné, slot_number, capacité automatiques).
                </p>
                {missingSlotsLoading && <div className="text-xs text-[#575A57]">Chargement…</div>}
                <div className="max-h-80 overflow-y-auto border border-[#E5E5E0] rounded">
                  {(missingSlotsDialog?.missing || []).length === 0 ? (
                    <div className="p-4 text-center text-sm text-[#575A57]">Aucun slot à restaurer.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F4F0] text-xs text-[#575A57]">
                        <tr>
                          <th className="px-3 py-2 text-left">Slot</th>
                          <th className="px-3 py-2 text-left">Produit</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(missingSlotsDialog?.missing || []).map((m) => (
                          <tr key={m.slot_number} className="border-t border-[#F0F0EB]">
                            <td className="px-3 py-2 font-mono font-semibold">{m.slot_number}</td>
                            <td className="px-3 py-2">
                              <div>{m.product_name}</div>
                              {m.product_sku && <div className="text-[11px] text-[#575A57] font-mono">{m.product_sku}</div>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Button
                                size="sm"
                                onClick={() => restoreSlot(missingSlotsDialog.distributor_id, m.slot_number)}
                                className="bg-[#1F4E3D] hover:bg-[#173D2F]"
                                data-testid={`restore-slot-${m.slot_number}`}
                              >
                                + Restaurer
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="missing-slots-close">Fermer</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Totaux globaux */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex flex-wrap gap-6 text-sm">
          <div>
            <div className="text-xs text-[#575A57] uppercase">Distributeurs</div>
            <div className="text-2xl font-bold text-[#1A1C1A] font-mono">{distribsList.length}</div>
          </div>
          <div>
            <div className="text-xs text-[#575A57] uppercase">Total planifié</div>
            <div className="text-2xl font-bold text-[#1A1C1A] font-mono">{totalPlanned.toFixed(0)}</div>
          </div>
          <div>
            <div className="text-xs text-[#575A57] uppercase">Total chargé (box thermo)</div>
            <div className="text-2xl font-bold text-[#1F4E3D] font-mono">{totalLoaded.toFixed(0)}</div>
          </div>
          {tour.notes && (
            <div className="flex-1 text-[#575A57]"><strong>Notes :</strong> {tour.notes}</div>
          )}
        </CardContent>
      </Card>

      {/* Tables groupées par distributeur */}
      <div className="printable-area space-y-6">
        {/* En-tête imprimable */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Rapport de remplissage</h1>
          <div className="text-sm text-[#575A57]">
            Tournée {tour.reference} · {new Date(tour.created_at).toLocaleString("fr-CH")} · Opérateur : {tour.created_by_name || "—"}
          </div>
        </div>

        {distribsList.map((g) => {
          // Phase 92 — Garde-fou : si le distrib reçoit < seuil minimal, badge + bouton "Exclure"
          const distribPlanned = g.lines.reduce((s, ln) => s + Number(ln.qty_planned || 0), 0);
          const distribLoaded = g.lines.reduce((s, ln) => s + Number(ln.qty_loaded || 0), 0);
          const isTooFew = distribPlanned > 0 && distribPlanned < MIN_QTY_PER_DISTRIB;
          const canExclude = !readOnly && tour.status === "BROUILLON";
          return (
          <Card key={g.distributor_id} className={`break-inside-avoid ${isTooFew ? "border-[#C1A063] border-2" : "border-[#E5E5E0]"}`} data-testid={`distrib-card-${g.distributor_id}`}>
            <CardContent className="p-0">
              <div className={`px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap ${isTooFew ? "bg-[#FBF4E9]" : "bg-[#F4F4F0]"}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#1A1C1A] flex items-center gap-2 flex-wrap">
                    {g.distributor_name}
                    {isTooFew && (
                      <Badge className="bg-[#C1A063] text-white text-[10px] no-print" data-testid={`badge-too-few-${g.distributor_id}`}>
                        ⚠ Trop peu — {distribPlanned} unité{distribPlanned > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#575A57]">
                    {g.distributor_code && <span className="font-mono mr-2">{g.distributor_code}</span>}
                    {g.distributor_city}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-[#575A57]">
                    <span className="font-mono">{distribPlanned.toFixed(0)}</span> planifié ·
                    <span className="font-mono text-[#1F4E3D] ml-1">{distribLoaded.toFixed(0)}</span> chargé
                  </div>
                  {/* Phase 92 — PDF livreur (1 page A4 par distrib) — dispo aussi en EN_LIVRAISON pour réimpression */}
                  {distribPlanned > 0 && tour.status !== "CLOTUREE" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDistribPdf(g.distributor_id)}
                      data-testid={`pdf-livreur-btn-${g.distributor_id}`}
                      className="no-print"
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1.5" />
                      PDF livreur
                    </Button>
                  )}
                  {/* Phase 101gf — Récupération d'un slot effacé par erreur (bouton visible en BROUILLON uniquement) */}
                  {canExclude && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openMissingSlots(g.distributor_id, g.distributor_name)}
                      data-testid={`restore-slot-btn-${g.distributor_id}`}
                      className="no-print text-[#1F4E3D] hover:bg-[#EAF4EE] border-[#1F4E3D]/40"
                      title="Récupérer un slot effacé par erreur (depuis la config du distributeur)"
                    >
                      <Layers className="w-3.5 h-3.5 mr-1.5" />
                      + Slot manquant
                    </Button>
                  )}
                  {/* Phase 92 — Exclure le distrib (uniquement en BROUILLON) */}
                  {canExclude && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => excludeDistrib(g.distributor_id, g.distributor_name, distribPlanned)}
                      data-testid={`exclude-distrib-btn-${g.distributor_id}`}
                      className="no-print text-[#C05C47] hover:bg-[#FBEEEA] hover:text-[#A04E2C] border-[#E8B0A0]"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" />
                      Exclure
                    </Button>
                  )}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF7] text-[#575A57] text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Slot</th>
                    <th className="text-left px-3 py-2 font-semibold">Produit</th>
                    <th className="text-right px-3 py-2 font-semibold">Actuel</th>
                    <th className="text-right px-3 py-2 font-semibold">Capa</th>
                    <th className="text-right px-3 py-2 font-semibold">Sugg.</th>
                    <th className="text-right px-3 py-2 font-semibold">Planifié</th>
                    <th className="text-left px-3 py-2 font-semibold no-print">Lots Conthey (choix DLC)</th>
                    <th className="text-right px-3 py-2 font-semibold no-print w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((ln) => {
                    const centralAvail = Number(ln.central_available || 0);
                    const totalLoadedProd = loadedByProduct[ln.product_id] || 0;
                    const remainingCentral = centralAvail - totalLoadedProd;
                    const overLoaded = remainingCentral < 0;
                    const allocations = ln.lot_allocations || [];
                    const totalAlloc = allocations.reduce((s, a) => s + Number(a.qty || 0), 0);
                    const qtyTarget = Number(ln.qty_planned || 0);
                    const allocOk = qtyTarget > 0 && Math.abs(totalAlloc - qtyTarget) < 0.001;
                    const testKey = `${g.distributor_code || g.distributor_id.slice(0,6)}-${ln.slot_number || ln.product_sku || "x"}`;
                    return (
                      <tr key={`${ln.distributor_id}-${ln.product_id}-${ln.slot_number || ""}`} className="border-t border-[#E5E5E0]" data-testid={`refill-line-${testKey}`}>
                        <td className="px-3 py-2">
                          {ln.slot_number ? (
                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-[#1F4E3D] text-white font-mono text-xs font-bold" data-testid={`slot-${testKey}`}>{ln.slot_number}</span>
                          ) : (
                            <span className="text-[#A3A6A3] text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#1A1C1A]">{ln.product_name}</div>
                          {ln.product_sku && <div className="text-xs text-[#A3A6A3] font-mono">{ln.product_sku}</div>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{Number(ln.current_qty).toFixed(0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#A3A6A3]">{Number(ln.capacity_max).toFixed(0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#A3A6A3]">{Number(ln.qty_suggested).toFixed(0)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1 no-print">
                            {/* Phase 98aw (10/07/2026) — Bouton -1 pour décrémenter facilement
                                si la préparatrice scanne une fois de trop (tapper le nombre
                                à la main sur tablette est pénible). */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const next = Math.max(0, Number(ln.qty_planned || 0) - 1);
                                updateLine(ln.distributor_id, ln.product_id, ln.slot_number, { qty_planned: next, qty_loaded: next });
                              }}
                              disabled={readOnly || Number(ln.qty_planned || 0) <= 0}
                              className="h-9 w-9 p-0 border-[#C05C47]/40 text-[#C05C47] hover:bg-[#FBEEEA] hover:text-[#A04E2C]"
                              data-testid={`qty-minus-${testKey}`}
                              title="Décrémenter d'une unité"
                            >
                              −
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={ln.qty_planned ?? 0}
                              onChange={(e) => updateLine(ln.distributor_id, ln.product_id, ln.slot_number, { qty_planned: Number(e.target.value || 0), qty_loaded: Number(e.target.value || 0) })}
                              disabled={readOnly}
                              className="w-16 text-right font-mono"
                              data-testid={`qty-planned-${testKey}`}
                            />
                          </div>
                          <span className="hidden print:inline font-mono">{Number(ln.qty_planned || 0).toFixed(0)}</span>
                        </td>
                        <td className="px-3 py-2 no-print">
                          {ln.stock_simple ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-[#EAF1F6] text-[#2E6A8E] border border-[#BCD3E3]" data-testid={`drink-badge-${testKey}`}>
                              Boisson · sans lot{ln.nayax_recommended_qty != null && <> · Nayax {Number(ln.nayax_recommended_qty).toFixed(0)}</>}
                            </span>
                          ) : allocations.length > 0 ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {allocations.slice(0, 3).map((a, i) => (
                                <Badge
                                  key={a.lot_id + i}
                                  className={`font-mono text-[10px] ${a.dlc && new Date(a.dlc) < new Date(Date.now() + 3*86400000) ? "bg-[#FBEEEA] text-[#C05C47] border-[#E8B0A0]" : "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]"}`}
                                  variant="outline"
                                  title={`${a.lot_number} · DLC ${a.dlc || "—"} · ${a.qty}u`}
                                >
                                  {a.lot_number || a.lot_id.slice(0,4)} × {a.qty}
                                </Badge>
                              ))}
                              {allocations.length > 3 && (
                                <span className="text-xs text-[#575A57]">+{allocations.length - 3}</span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setLotDialogLine({
                                  distributor_id: ln.distributor_id,
                                  product_id: ln.product_id,
                                  product_name: ln.product_name,
                                  slot_number: ln.slot_number,
                                  qty_target: qtyTarget,
                                  initialAllocations: allocations,
                                })}
                                disabled={readOnly}
                                className="h-6 px-2 text-xs"
                                data-testid={`lot-select-btn-${testKey}`}
                              >
                                <Layers className="w-3 h-3 mr-1" /> Modifier
                              </Button>
                              {!allocOk && qtyTarget > 0 && (
                                <span className="text-[10px] text-[#C05C47] font-mono">≠ {qtyTarget}</span>
                              )}
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setLotDialogLine({
                                distributor_id: ln.distributor_id,
                                product_id: ln.product_id,
                                product_name: ln.product_name,
                                slot_number: ln.slot_number,
                                qty_target: qtyTarget,
                                initialAllocations: [],
                              })}
                              disabled={readOnly || qtyTarget === 0}
                              className="h-8 text-xs"
                              data-testid={`lot-select-btn-${testKey}`}
                            >
                              <Layers className="w-3.5 h-3.5 mr-1" /> Choisir les lots ({qtyTarget || 0})
                            </Button>
                          )}
                        </td>
                        <td className="px-3 py-2 no-print">
                          {!readOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteLine(ln)}
                              className="h-7 w-7 hover:bg-[#FBEEEA]"
                              title="Supprimer cette ligne"
                              data-testid={`delete-line-btn-${testKey}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-[#C05C47]" />
                            </Button>
                          )}
                          {overLoaded && (
                            <div className="text-[10px] text-[#C05C47] font-mono flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Central {remainingCentral.toFixed(0)}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* CSS impression */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, html { background: white !important; }
          .printable-area { padding: 10mm; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          input[type="number"] { border: none !important; padding: 0 !important; background: transparent !important; }
        }
      `}</style>

      {/* Phase 98 — Dialog de choix des lots Conthey */}
      {lotDialogLine && (
        <LotAllocationDialog
          open={!!lotDialogLine}
          onOpenChange={(o) => !o && setLotDialogLine(null)}
          centralDepotId={tour.central_depot_id}
          productId={lotDialogLine.product_id}
          productName={lotDialogLine.product_name}
          slotNumber={lotDialogLine.slot_number}
          qtyTarget={lotDialogLine.qty_target}
          initialAllocations={lotDialogLine.initialAllocations}
          onConfirm={saveLotAllocations}
        />
      )}

      {/* Phase 98ay — Diagnostic des lignes fantômes */}
      <PhantomLinesDiagnostic
        tourId={id}
        open={phantomOpen}
        onClose={() => setPhantomOpen(false)}
        onCleaned={load}
      />
    </div>
  );
}
